import {
	Alert,
	App as AntdApp,
	Button,
	Checkbox,
	Empty,
	Input,
	Modal,
	Select,
	Spin,
	Switch,
	Typography,
} from 'antd';
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	FilterOutlined,
	PlusOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	createTimedSnapshotTask,
	deleteTimedSnapshotTask,
	fetchTimedSnapshotDevices,
	fetchTimedSnapshotOperatorRank,
	fetchTimedSnapshotOperatorStatistic,
	fetchTimedSnapshotPictures,
	fetchTimedSnapshotPlanList,
	fetchTimedSnapshotPlans,
	fetchTimedSnapshotStoreStatistic,
	fetchTimedSnapshotStores,
	fetchTimedSnapshotTemplateItems,
	fetchTimedSnapshotUsers,
	fetchTimedSnapshotViolationRanking,
	getTimedSnapshotDeviceName,
	getTimedSnapshotPictureUrl,
	getTimedSnapshotPlanId,
	getTimedSnapshotPlanTitle,
	getTimedSnapshotStoreName,
	getTimedSnapshotUserName,
	handleTimedSnapshotPicture,
	isTimedSnapshotPlanEnabled,
	setTimedSnapshotTaskEnabled,
	type TimedSnapshotDevice,
	type TimedSnapshotOperatorRankRow,
	type TimedSnapshotOperatorStatistic,
	type TimedSnapshotPicture,
	type TimedSnapshotPlan,
	type TimedSnapshotStore,
	type TimedSnapshotStoreStatistic,
	type TimedSnapshotTemplateItem,
	type TimedSnapshotUser,
	type TimedSnapshotViolationRankRow,
} from '../api/timed-snapshot.js';
import { useRegion } from '../hooks/useRegion.js';
import { NativeMirrorPage, NativeTopBar } from '../components/MobileRootScaffold.js';
import { resolveExternalImageUrl } from '../utils/media-url.js';

const PRIMARY = '#d18a36';
const TEAL = '#63b7af';
const TEXT = '#2f343b';
const MUTED = '#9aa0a6';
const BG = '#f5f5f5';
const ALL_WEEK = ['1', '2', '3', '4', '5', '6', '7'];
const ALL_MONTHS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

type MainTab = 'statistics' | 'data' | 'task';
type StatsTab = 'store' | 'executor';
type DataTab = 'overdue' | 'unprocessed' | 'processed';
type View = 'main' | 'create' | 'picture';

function pad(value: number): string {
	return String(value).padStart(2, '0');
}

function formatLocalDate(date: Date): string {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function addDays(days: number): string {
	const date = new Date();
	date.setDate(date.getDate() + days);
	return formatLocalDate(date);
}

function formatNativeDate(value: string): string {
	const [year, month, day] = value.split('-');
	if (!year || !month || !day) return value;
	return `${day}-${month}-${year}`;
}

function formatNativeDateTime(value: unknown): string {
	if (value == null) return '-';
	const text = String(value);
	return text.length >= 16 ? text.slice(0, 16) : text || '-';
}

function tDefault(t: (key: string, options?: Record<string, unknown>) => string, key: string, fallback: string): string {
	return t(key, { defaultValue: fallback });
}

function emptyStoreStats(): TimedSnapshotStoreStatistic {
	return {
		totalViolations: 0,
		mostViolationStoreCount: 0,
		leastViolationStoreCount: 0,
		mostViolationItemCount: 0,
		leastViolationItemCount: 0,
		dailyTrendByItem: [],
		dailyTrendByStore: [],
	};
}

function emptyOperatorStats(): TimedSnapshotOperatorStatistic {
	return { shouldScreenshot: 0, hasScreenshot: 0, handled: 0, overdue: 0 };
}

export default function SnapshotInspection() {
	const region = useRegion();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { message } = AntdApp.useApp();
	const [view, setView] = useState<View>('main');
	const [mainTab, setMainTab] = useState<MainTab>('statistics');
	const [statsTab, setStatsTab] = useState<StatsTab>('store');
	const [dataTab, setDataTab] = useState<DataTab>('overdue');
	const [startDate, setStartDate] = useState(() => addDays(-6));
	const [endDate, setEndDate] = useState(() => addDays(0));
	const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
	const [selectedPlanId, setSelectedPlanId] = useState<string>('');
	const [taskStatus, setTaskStatus] = useState<string>('');
	const [stores, setStores] = useState<TimedSnapshotStore[]>([]);
	const [planOptions, setPlanOptions] = useState<TimedSnapshotPlan[]>([]);
	const [templateItems, setTemplateItems] = useState<TimedSnapshotTemplateItem[]>([]);
	const [plans, setPlans] = useState<TimedSnapshotPlan[]>([]);
	const [pictures, setPictures] = useState<TimedSnapshotPicture[]>([]);
	const [storeStats, setStoreStats] = useState<TimedSnapshotStoreStatistic>(emptyStoreStats);
	const [operatorStats, setOperatorStats] = useState<TimedSnapshotOperatorStatistic>(emptyOperatorStats);
	const [operatorRows, setOperatorRows] = useState<TimedSnapshotOperatorRankRow[]>([]);
	const [itemRanking, setItemRanking] = useState<TimedSnapshotViolationRankRow[]>([]);
	const [storeRanking, setStoreRanking] = useState<TimedSnapshotViolationRankRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [selectedPicture, setSelectedPicture] = useState<TimedSnapshotPicture | null>(null);
	const [statusLoadingId, setStatusLoadingId] = useState<string | null>(null);
	const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState(0);

	const selectedStores = useMemo(
		() => stores.filter((store) => selectedStoreIds.includes(String(store.store_id))),
		[stores, selectedStoreIds],
	);

	const loadSharedFilters = useCallback(async () => {
		try {
			const [nextStores, nextPlans, nextTemplates] = await Promise.all([
				fetchTimedSnapshotStores(),
				fetchTimedSnapshotPlanList(),
				fetchTimedSnapshotTemplateItems(),
			]);
			setStores(nextStores);
			setPlanOptions(nextPlans.items);
			setTemplateItems(nextTemplates);
		} catch {
			// Main content loaders surface their own errors. Filter lists are optional.
		}
	}, []);

	useEffect(() => {
		if (region !== 'apac') return;
		void loadSharedFilters();
	}, [loadSharedFilters, region]);

	const loadStatistics = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			if (statsTab === 'store') {
				const [summary, itemRank, storeRank] = await Promise.all([
					fetchTimedSnapshotStoreStatistic({ startDate, endDate, storeIds: selectedStoreIds }),
					fetchTimedSnapshotViolationRanking('items', { startDate, endDate, storeIds: selectedStoreIds, count: 5 }),
					fetchTimedSnapshotViolationRanking('stores', { startDate, endDate, storeIds: selectedStoreIds, count: 5 }),
				]);
				setStoreStats(summary);
				setItemRanking(itemRank.items);
				setStoreRanking(storeRank.items);
			} else {
				const [summary, rows] = await Promise.all([
					fetchTimedSnapshotOperatorStatistic({
						startDate,
						endDate,
						storeIds: selectedStoreIds,
						planId: selectedPlanId || undefined,
					}),
					fetchTimedSnapshotOperatorRank({
						startDate,
						endDate,
						storeIds: selectedStoreIds,
						planId: selectedPlanId || undefined,
						count: 20,
					}),
				]);
				setOperatorStats(summary);
				setOperatorRows(rows.items);
			}
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setLoading(false);
		}
	}, [endDate, selectedPlanId, selectedStoreIds, startDate, statsTab]);

	const loadPictures = useCallback(async () => {
		setLoading(true);
		setError(null);
		const result = await fetchTimedSnapshotPictures({
			tab: dataTab,
			startDate,
			endDate,
			storeIds: selectedStoreIds,
			planId: selectedPlanId || undefined,
			count: 30,
		});
		if (!result.ok) {
			setError(result.msg || tDefault(t, 'native.snapshot.load_failed', 'Load failed'));
			setPictures([]);
		} else {
			setPictures(result.items);
		}
		setLoading(false);
	}, [dataTab, endDate, selectedPlanId, selectedStoreIds, startDate, t]);

	const loadTasks = useCallback(async () => {
		setLoading(true);
		setError(null);
		const result = await fetchTimedSnapshotPlans({
			count: 20,
			status: taskStatus || undefined,
			storeId: selectedStoreIds[0],
		});
		if (!result.ok) {
			setError(result.msg || tDefault(t, 'native.snapshot.load_failed', 'Load failed'));
			setPlans([]);
		} else {
			setPlans(result.items);
		}
		setLoading(false);
	}, [selectedStoreIds, taskStatus, t]);

	useEffect(() => {
		if (region !== 'apac' || view !== 'main') return;
		if (mainTab === 'statistics') void loadStatistics();
		if (mainTab === 'data') void loadPictures();
		if (mainTab === 'task') void loadTasks();
	}, [loadPictures, loadStatistics, loadTasks, mainTab, region, reloadKey, view]);

	function goBack() {
		if (view === 'picture') {
			setSelectedPicture(null);
			setView('main');
			return;
		}
		if (view === 'create') {
			setView('main');
			setMainTab('task');
			return;
		}
		navigate('/m/application', { replace: true });
	}

	async function toggleTask(plan: TimedSnapshotPlan, enabled: boolean) {
		const planId = getTimedSnapshotPlanId(plan);
		if (planId == null) return;
		setStatusLoadingId(String(planId));
		const result = await setTimedSnapshotTaskEnabled(planId, enabled);
		setStatusLoadingId(null);
		if (!result.ok) {
			message.error(result.msg || tDefault(t, 'native.snapshot.plan_status_failed', 'Plan status update failed'));
			return;
		}
		message.success(enabled
			? tDefault(t, 'native.snapshot.plan_opened_success', 'Plan opened successfully')
			: tDefault(t, 'native.snapshot.plan_closed_success', 'Plan closed successfully'));
		void loadTasks();
	}

	function confirmDelete(plan: TimedSnapshotPlan) {
		const title = getTimedSnapshotPlanTitle(plan);
		Modal.confirm({
			title: tDefault(t, 'native.snapshot.delete_task', 'Delete task'),
			content: title,
			okText: tDefault(t, 'native.common.delete', 'Delete'),
			okButtonProps: { danger: true },
			cancelText: tDefault(t, 'native.common.cancel', 'Cancel'),
			onOk: async () => {
				const planId = getTimedSnapshotPlanId(plan);
				if (planId == null) return;
				setDeleteLoadingId(String(planId));
				const result = await deleteTimedSnapshotTask(planId);
				setDeleteLoadingId(null);
				if (!result.ok) {
					message.error(result.msg || tDefault(t, 'native.snapshot.delete_failed', 'Delete failed'));
					return;
				}
				message.success(tDefault(t, 'native.snapshot.delete_success', 'Deleted successfully'));
				void loadTasks();
			},
		});
	}

	async function processPicture(picture: TimedSnapshotPicture, handleStatus: 1 | 2) {
		const item = extractPictureTemplateItem(picture) ?? templateItems[0];
		const id = picture.id ?? picture.pic_id;
		if (id == null) return;
		if (!item) {
			message.warning(tDefault(t, 'native.snapshot.validation_template', 'Please select an assessment template'));
			return;
		}
		const result = await handleTimedSnapshotPicture({
			id,
			handleStatus,
			item,
			withoutEvent: true,
			description: handleStatus === 1 ? 'Qualified from web' : 'Unqualified from web',
		});
		if (!result.ok) {
			message.error(result.msg || tDefault(t, 'native.snapshot.process_failed', 'Process failed'));
			return;
		}
		message.success(tDefault(t, 'native.snapshot.process_success', 'Processed successfully'));
		void loadPictures();
		void loadStatistics();
	}

	if (region !== 'apac') {
		return (
			<NativeMirrorPage style={{ background: '#fff' }}>
				<NativeTopBar title={tDefault(t, 'native.snapshot.timed_snapshot_inspection', 'Timed Snapshot Inspection')} onBack={goBack} backLabel="" />
				<Alert type="info" showIcon message={tDefault(t, 'native.snapshot.apac_only', 'Timed Snapshot Inspection is only connected for APAC.')} style={{ margin: 14 }} />
			</NativeMirrorPage>
		);
	}

	if (view === 'create') {
		return (
			<CreateInspectionView
				t={t}
				onBack={goBack}
				onSaved={() => {
					setView('main');
					setMainTab('task');
					setReloadKey((key) => key + 1);
				}}
				initialStores={stores}
			/>
		);
	}

	if (view === 'picture' && selectedPicture) {
		return (
			<PicturePreview
				t={t}
				picture={selectedPicture}
				stores={stores}
				onBack={goBack}
			/>
		);
	}

	return (
		<NativeMirrorPage style={{ background: BG, padding: 0 }}>
			<SnapshotTopBar
				t={t}
				active={mainTab}
				onBack={goBack}
				onTabChange={setMainTab}
				action={mainTab === 'data'
					? (
						<IconButton label={tDefault(t, 'native.common.filter', 'Filter')} onClick={() => setFilterOpen((value) => !value)}>
							<FilterOutlined />
						</IconButton>
					)
					: mainTab === 'task'
						? (
							<IconButton label={tDefault(t, 'native.snapshot.create_inspection', 'Create Inspection')} onClick={() => setView('create')}>
								<PlusOutlined />
							</IconButton>
						)
						: null
				}
			/>
			{filterOpen || mainTab !== 'statistics' ? (
				<FilterPanel
					t={t}
					mainTab={mainTab}
					startDate={startDate}
					endDate={endDate}
					stores={stores}
					plans={planOptions}
					selectedStoreIds={selectedStoreIds}
					selectedPlanId={selectedPlanId}
					taskStatus={taskStatus}
					onStartDate={setStartDate}
					onEndDate={setEndDate}
					onStoreIds={setSelectedStoreIds}
					onPlanId={setSelectedPlanId}
					onTaskStatus={setTaskStatus}
					onRefresh={() => setReloadKey((key) => key + 1)}
				/>
			) : null}
			{mainTab === 'statistics' && (
				<StatisticsView
					t={t}
					active={statsTab}
					onChange={setStatsTab}
					nativeRange={`${formatNativeDate(startDate)}~${formatNativeDate(endDate)}`}
					storeStats={storeStats}
					operatorStats={operatorStats}
					operatorRows={operatorRows}
					itemRanking={itemRanking}
					storeRanking={storeRanking}
					loading={loading}
					error={error}
					onRefresh={() => setReloadKey((key) => key + 1)}
				/>
			)}
			{mainTab === 'data' && (
				<InspectionDataView
					t={t}
					active={dataTab}
					onChange={setDataTab}
					pictures={pictures}
					loading={loading}
					error={error}
					onRefresh={() => setReloadKey((key) => key + 1)}
					onOpenPicture={(picture) => {
						setSelectedPicture(picture);
						setView('picture');
					}}
					onProcess={processPicture}
				/>
			)}
			{mainTab === 'task' && (
				<InspectionTaskView
					t={t}
					plans={plans}
					loading={loading}
					error={error}
					statusLoadingId={statusLoadingId}
					deleteLoadingId={deleteLoadingId}
					onRefresh={() => setReloadKey((key) => key + 1)}
					onCreate={() => setView('create')}
					onToggle={toggleTask}
					onDelete={confirmDelete}
				/>
			)}
		</NativeMirrorPage>
	);
}

function SnapshotTopBar({
	t,
	active,
	action,
	onBack,
	onTabChange,
}: {
	t: (key: string, options?: Record<string, unknown>) => string;
	active: MainTab;
	action: ReactNode;
	onBack: () => void;
	onTabChange: (tab: MainTab) => void;
}) {
	const tabs: Array<[MainTab, string]> = [
		['statistics', tDefault(t, 'native.snapshot.inspection_statistics', 'Inspection Statistics')],
		['data', tDefault(t, 'native.snapshot.inspection_data', 'Inspection Data')],
		['task', tDefault(t, 'native.snapshot.inspection_task', 'Inspection Task')],
	];
	return (
		<header style={topBarStyle}>
			<button type="button" aria-label={tDefault(t, 'common.back', 'Back')} onClick={onBack} style={backButtonStyle}>
				<span aria-hidden="true" style={backChevronStyle} />
			</button>
			<div style={topTabsStyle}>
				{tabs.map(([key, label]) => (
					<button
						key={key}
						type="button"
						onClick={() => onTabChange(key)}
						style={{
							...topTabButtonStyle,
							color: active === key ? PRIMARY : TEXT,
							fontWeight: active === key ? 650 : 500,
						}}
					>
						{label}
						{active === key && <span style={topTabLineStyle} />}
					</button>
				))}
			</div>
			<div style={{ minWidth: 0 }}>{action}</div>
		</header>
	);
}

function FilterPanel({
	t,
	mainTab,
	startDate,
	endDate,
	stores,
	plans,
	selectedStoreIds,
	selectedPlanId,
	taskStatus,
	onStartDate,
	onEndDate,
	onStoreIds,
	onPlanId,
	onTaskStatus,
	onRefresh,
}: {
	t: (key: string, options?: Record<string, unknown>) => string;
	mainTab: MainTab;
	startDate: string;
	endDate: string;
	stores: TimedSnapshotStore[];
	plans: TimedSnapshotPlan[];
	selectedStoreIds: string[];
	selectedPlanId: string;
	taskStatus: string;
	onStartDate: (value: string) => void;
	onEndDate: (value: string) => void;
	onStoreIds: (value: string[]) => void;
	onPlanId: (value: string) => void;
	onTaskStatus: (value: string) => void;
	onRefresh: () => void;
}) {
	return (
		<div style={filterPanelStyle}>
			{mainTab !== 'task' && (
				<>
					<DateField label={tDefault(t, 'native.snapshot.start_date', 'Start Date')} value={startDate} onChange={onStartDate} />
					<DateField label={tDefault(t, 'native.snapshot.end_date', 'End Date')} value={endDate} onChange={onEndDate} />
				</>
			)}
			<Select
				mode="multiple"
				allowClear
				value={selectedStoreIds}
				onChange={(value) => onStoreIds(value.map(String))}
				placeholder={tDefault(t, 'native.common.all_stores', 'All Stores')}
				style={{ minWidth: 160, flex: '1 1 160px' }}
				options={stores.map((store) => ({ value: String(store.store_id), label: getTimedSnapshotStoreName(store) }))}
			/>
			{mainTab !== 'task' && (
				<Select
					allowClear
					value={selectedPlanId || undefined}
					onChange={(value) => onPlanId(value ? String(value) : '')}
					placeholder={tDefault(t, 'native.common.all_tasks', 'All Tasks')}
					style={{ minWidth: 160, flex: '1 1 160px' }}
					options={plans.map((plan) => ({ value: String(getTimedSnapshotPlanId(plan)), label: getTimedSnapshotPlanTitle(plan) }))}
				/>
			)}
			{mainTab === 'task' && (
				<Select
					allowClear
					value={taskStatus || undefined}
					onChange={(value) => onTaskStatus(value ? String(value) : '')}
					placeholder={tDefault(t, 'native.snapshot.task_status', 'Task Status')}
					style={{ minWidth: 150, flex: '1 1 150px' }}
					options={[
						{ value: '0', label: tDefault(t, 'native.snapshot.enabled', 'Enabled') },
						{ value: '1', label: tDefault(t, 'native.snapshot.disabled', 'Disabled') },
					]}
				/>
			)}
			<Button icon={<ReloadOutlined />} onClick={onRefresh}>
				{tDefault(t, 'native.common.refresh', 'Refresh')}
			</Button>
		</div>
	);
}

function DateField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
	return (
		<label style={{ display: 'grid', gap: 4, minWidth: 132, flex: '1 1 132px' }}>
			<span style={{ color: MUTED, fontSize: 12 }}>{label}</span>
			<input type="date" value={value} onChange={(event) => onChange(event.currentTarget.value)} style={dateInputStyle} />
		</label>
	);
}

function StatisticsView({
	t,
	active,
	nativeRange,
	storeStats,
	operatorStats,
	operatorRows,
	itemRanking,
	storeRanking,
	loading,
	error,
	onChange,
	onRefresh,
}: {
	t: (key: string, options?: Record<string, unknown>) => string;
	active: StatsTab;
	nativeRange: string;
	storeStats: TimedSnapshotStoreStatistic;
	operatorStats: TimedSnapshotOperatorStatistic;
	operatorRows: TimedSnapshotOperatorRankRow[];
	itemRanking: TimedSnapshotViolationRankRow[];
	storeRanking: TimedSnapshotViolationRankRow[];
	loading: boolean;
	error: string | null;
	onChange: (tab: StatsTab) => void;
	onRefresh: () => void;
}) {
	return (
		<div>
			<div style={subTabsStyle}>
				<SubTab active={active === 'store'} onClick={() => onChange('store')}>{tDefault(t, 'native.snapshot.store_data_statistics', 'Store Data Statistics')}</SubTab>
				<SubTab active={active === 'executor'} onClick={() => onChange('executor')}>{tDefault(t, 'native.snapshot.executor_statistics', 'Executor Statistics')}</SubTab>
			</div>
			<div style={nativeFilterLineStyle}>{nativeRange}<span aria-hidden="true">⌄</span></div>
			{error && <Alert type="warning" showIcon message={error} style={{ margin: 12 }} />}
			{loading ? <CenteredSpin /> : active === 'store' ? (
				<>
					<BlueMetric label={tDefault(t, 'native.snapshot.total_violations', 'Total Violations')} value={storeStats.totalViolations} />
					<QuadMetrics items={[
						[tDefault(t, 'native.snapshot.maximum_violations', 'Maximum Violations'), storeStats.mostViolationStoreCount],
						[tDefault(t, 'native.snapshot.minimum_violations', 'Minimum Violations'), storeStats.leastViolationStoreCount],
						[tDefault(t, 'native.snapshot.maximum_violations', 'Maximum Violations'), storeStats.mostViolationItemCount],
						[tDefault(t, 'native.snapshot.minimum_violations', 'Minimum Violations'), storeStats.leastViolationItemCount],
					]} />
					<SectionTitle title={tDefault(t, 'native.snapshot.ranking', 'Ranking')} />
					<RankTable t={t} rows={storeRanking} />
					<SectionTitle title={tDefault(t, 'native.snapshot.violation_items_ranking', 'Violations Items Ranking')} />
					<RankTable t={t} rows={itemRanking} />
					<TrendBlock title={tDefault(t, 'native.snapshot.daily_violation_item', 'Daily Violation Trend (By Violation Item)')} rows={storeStats.dailyTrendByItem} t={t} />
					<TrendBlock title={tDefault(t, 'native.snapshot.daily_violation_store', 'Daily Violation Trend (By Store)')} rows={storeStats.dailyTrendByStore} t={t} />
				</>
			) : (
				<>
					<BlueMetric label={tDefault(t, 'native.snapshot.all_planned_screenshot', 'All Planned Screenshot')} value={operatorStats.shouldScreenshot} />
					<TripleMetrics items={[
						[tDefault(t, 'native.snapshot.current_screenshot', 'Current Screenshot'), operatorStats.hasScreenshot],
						[tDefault(t, 'native.snapshot.current_processed', 'Current Processed'), operatorStats.handled],
						[tDefault(t, 'native.snapshot.overdue_unprocessed', 'Overdue Unprocessed'), operatorStats.overdue],
					]} />
					<OperatorTable t={t} rows={operatorRows} />
				</>
			)}
			<Button block type="text" icon={<ReloadOutlined />} onClick={onRefresh} style={{ marginTop: 10, color: PRIMARY }}>
				{tDefault(t, 'native.common.refresh', 'Refresh')}
			</Button>
		</div>
	);
}

function InspectionDataView({
	t,
	active,
	pictures,
	loading,
	error,
	onChange,
	onRefresh,
	onOpenPicture,
	onProcess,
}: {
	t: (key: string, options?: Record<string, unknown>) => string;
	active: DataTab;
	pictures: TimedSnapshotPicture[];
	loading: boolean;
	error: string | null;
	onChange: (tab: DataTab) => void;
	onRefresh: () => void;
	onOpenPicture: (picture: TimedSnapshotPicture) => void;
	onProcess: (picture: TimedSnapshotPicture, handleStatus: 1 | 2) => void;
}) {
	const groups = groupPicturesByDate(pictures);
	return (
		<div>
			<div style={dataTabsStyle}>
				<SubTab active={active === 'overdue'} onClick={() => onChange('overdue')}>{tDefault(t, 'native.snapshot.overdue_unprocessed', 'Overdue Unprocessed')}</SubTab>
				<SubTab active={active === 'unprocessed'} onClick={() => onChange('unprocessed')}>{tDefault(t, 'native.snapshot.all_unprocessed', 'All Unprocessed')}</SubTab>
				<SubTab active={active === 'processed'} onClick={() => onChange('processed')}>{tDefault(t, 'native.snapshot.processed', 'Processed')}</SubTab>
			</div>
			{error && <Alert type="warning" showIcon message={error} style={{ margin: 12 }} />}
			{loading ? <CenteredSpin /> : pictures.length === 0 ? (
				<NativeEmpty t={t} onRefresh={onRefresh} />
			) : (
				<div style={{ background: '#fff', padding: '16px 12px' }}>
					{groups.map((group) => (
						<div key={group.label} style={timelineGroupStyle}>
							<span style={timelineMarkerStyle} />
							<div style={timelineLabelStyle}>{group.label}</div>
							<div style={pictureGridStyle}>
								{group.items.map((picture) => (
									<div key={String(picture.id ?? picture.pic_id ?? getTimedSnapshotPictureUrl(picture))} style={pictureCardStyle}>
										<button type="button" onClick={() => onOpenPicture(picture)} style={pictureButtonStyle}>
											<PictureImage picture={picture} />
											<span style={pictureCaptionStyle}>{picture.store_name || picture.alias || picture.item_name || '-'}</span>
											<span style={pictureTimeStyle}>{formatNativeDateTime(picture.screenshot_time || picture.create_time)}</span>
										</button>
										{active !== 'processed' && (
											<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
												<Button size="small" icon={<CheckCircleOutlined />} onClick={() => onProcess(picture, 1)}>
													{tDefault(t, 'native.snapshot.qualified', 'Qualified')}
												</Button>
												<Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => onProcess(picture, 2)}>
													{tDefault(t, 'native.snapshot.unqualified', 'Unqualified')}
												</Button>
											</div>
										)}
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function InspectionTaskView({
	t,
	plans,
	loading,
	error,
	statusLoadingId,
	deleteLoadingId,
	onRefresh,
	onCreate,
	onToggle,
	onDelete,
}: {
	t: (key: string, options?: Record<string, unknown>) => string;
	plans: TimedSnapshotPlan[];
	loading: boolean;
	error: string | null;
	statusLoadingId: string | null;
	deleteLoadingId: string | null;
	onRefresh: () => void;
	onCreate: () => void;
	onToggle: (plan: TimedSnapshotPlan, enabled: boolean) => void;
	onDelete: (plan: TimedSnapshotPlan) => void;
}) {
	if (loading) return <CenteredSpin />;
	if (error) return <Alert type="warning" showIcon message={error} style={{ margin: 12 }} />;
	if (plans.length === 0) {
		return (
			<NativeEmpty t={t} onRefresh={onRefresh}>
				<Button type="primary" icon={<PlusOutlined />} onClick={onCreate} style={{ background: TEAL, borderColor: TEAL }}>
					{tDefault(t, 'native.snapshot.create_inspection', 'Create Inspection')}
				</Button>
			</NativeEmpty>
		);
	}
	return (
		<div style={{ background: '#fff' }}>
			{plans.map((plan) => {
				const planId = String(getTimedSnapshotPlanId(plan) ?? getTimedSnapshotPlanTitle(plan));
				const enabled = isTimedSnapshotPlanEnabled(plan);
				return (
					<article key={planId} style={taskCardStyle}>
						<div style={taskTitleRowStyle}>
							<span style={{ ...statusDotStyle, background: enabled ? '#47d96d' : '#e9573f' }} />
							<Typography.Text style={taskTitleStyle} ellipsis>{getTimedSnapshotPlanTitle(plan)}</Typography.Text>
							<span style={{ color: enabled ? '#55c99b' : '#b25f51', fontSize: 17 }}>
								{enabled ? tDefault(t, 'native.snapshot.enabled', 'Enabled') : tDefault(t, 'native.snapshot.disabled', 'Disabled')}
							</span>
							<Switch size="small" checked={enabled} loading={statusLoadingId === planId} onChange={(checked) => onToggle(plan, checked)} />
						</div>
						<div style={taskCountersStyle}>
							<TaskCounter value={numberFromRecord(plan.today_count, ['should_screenshot_count'])} label={tDefault(t, 'native.snapshot.today_screenshots', 'Today Screenshots')} />
							<TaskCounter value={numberFromRecord(plan.today_count, ['had_handle_count'])} label={tDefault(t, 'native.snapshot.today_processed', 'Today Processed')} />
							<TaskCounter value={numberFromRecord(plan.total_count, ['total_untreated'])} label={tDefault(t, 'native.snapshot.total_unprocessed', 'Total Unprocessed')} />
							<TaskCounter value={numberFromRecord(plan.total_count, ['total_unqualified'])} label={tDefault(t, 'native.snapshot.total_unqualified', 'Total Unqualified')} />
						</div>
						<div style={taskInfoBoxStyle}>
							<div>{tDefault(t, 'native.snapshot.task_store', 'Task Store')}: {plan.store_count ?? getPlanStoreCount(plan)}{tDefault(t, 'native.snapshot.stores_suffix', 'Stores')}</div>
							<div>{tDefault(t, 'native.snapshot.executor', 'Executor')}: {plan.execute_count ?? '-'}{tDefault(t, 'native.snapshot.people_suffix', 'People')}</div>
							<div>{tDefault(t, 'native.snapshot.task_time', 'Task Time')}: {getPlanDateRange(plan)}</div>
							<div>{tDefault(t, 'native.snapshot.execution_cycle', 'Execution Cycle')}: {getPlanCycle(plan, t)}</div>
						</div>
						<div style={taskActionsStyle}>
							<Button size="small" icon={<ReloadOutlined />} onClick={onRefresh}>
								{tDefault(t, 'native.common.refresh', 'Refresh')}
							</Button>
							<Button size="small" danger loading={deleteLoadingId === planId} onClick={() => onDelete(plan)}>
								{tDefault(t, 'native.common.delete', 'Delete')}
							</Button>
						</div>
					</article>
				);
			})}
		</div>
	);
}

function CreateInspectionView({
	t,
	onBack,
	onSaved,
	initialStores,
}: {
	t: (key: string, options?: Record<string, unknown>) => string;
	onBack: () => void;
	onSaved: () => void;
	initialStores: TimedSnapshotStore[];
}) {
	const { message } = AntdApp.useApp();
	const [title, setTitle] = useState('');
	const [startDate, setStartDate] = useState(() => addDays(0));
	const [endDate, setEndDate] = useState(() => addDays(7));
	const [week, setWeek] = useState<string[]>(ALL_WEEK);
	const [month, setMonth] = useState<string[]>(ALL_MONTHS);
	const [stores, setStores] = useState<TimedSnapshotStore[]>(initialStores);
	const [users, setUsers] = useState<TimedSnapshotUser[]>([]);
	const [devices, setDevices] = useState<TimedSnapshotDevice[]>([]);
	const [templates, setTemplates] = useState<TimedSnapshotTemplateItem[]>([]);
	const [storeIds, setStoreIds] = useState<string[]>([]);
	const [userIds, setUserIds] = useState<string[]>([]);
	const [deviceKeys, setDeviceKeys] = useState<string[]>([]);
	const [templateKey, setTemplateKey] = useState<string>('');
	const [times, setTimes] = useState<string[]>([nextTime()]);
	const [loading, setLoading] = useState(false);
	const [submitLoading, setSubmitLoading] = useState(false);

	useEffect(() => {
		let active = true;
		setLoading(true);
		void Promise.all([
			stores.length > 0 ? Promise.resolve(stores) : fetchTimedSnapshotStores(),
			fetchTimedSnapshotUsers(),
			fetchTimedSnapshotTemplateItems(),
		]).then(([nextStores, nextUsers, nextTemplates]) => {
			if (!active) return;
			setStores(nextStores);
			setUsers(nextUsers);
			setTemplates(nextTemplates);
			if (!templateKey && nextTemplates[0]) setTemplateKey(templateItemKey(nextTemplates[0]));
		}).catch((e: unknown) => {
			if (active) message.error(e instanceof Error ? e.message : String(e));
		}).finally(() => {
			if (active) setLoading(false);
		});
		return () => {
			active = false;
		};
	}, [message, stores, stores.length, templateKey]);

	useEffect(() => {
		let active = true;
		if (storeIds.length === 0) {
			setDevices([]);
			setDeviceKeys([]);
			return () => {
				active = false;
			};
		}
		void fetchTimedSnapshotDevices(storeIds).then((nextDevices) => {
			if (!active) return;
			setDevices(nextDevices);
			const validKeys = new Set(nextDevices.map(deviceKey));
			setDeviceKeys((previous) => previous.filter((key) => validKeys.has(key)));
		}).catch((e: unknown) => {
			if (active) message.error(e instanceof Error ? e.message : String(e));
		});
		return () => {
			active = false;
		};
	}, [message, storeIds]);

	const selectedStores = useMemo(
		() => stores.filter((store) => storeIds.includes(String(store.store_id))),
		[storeIds, stores],
	);
	const selectedUsers = useMemo(
		() => users.filter((user) => userIds.includes(String(user.user_id))),
		[userIds, users],
	);
	const selectedDevices = useMemo(
		() => devices.filter((device) => deviceKeys.includes(deviceKey(device))),
		[deviceKeys, devices],
	);
	const selectedTemplate = templates.find((item) => templateItemKey(item) === templateKey);

	async function save() {
		if (!selectedTemplate) {
			message.warning(tDefault(t, 'native.snapshot.validation_template', 'Please select an assessment template'));
			return;
		}
		setSubmitLoading(true);
		const result = await createTimedSnapshotTask({
			title,
			startDate,
			endDate,
			executeWeek: week,
			executeMonth: month,
			items: [selectedTemplate],
			stores: selectedStores,
			devices: selectedDevices,
			users: selectedUsers,
			times,
		});
		setSubmitLoading(false);
		if (!result.ok) {
			message.error(result.msg || tDefault(t, 'native.snapshot.save_failed', 'Save failed'));
			return;
		}
		message.success(tDefault(t, 'native.snapshot.save_success', 'Saved successfully'));
		onSaved();
	}

	return (
		<NativeMirrorPage style={{ background: '#fff', padding: 0 }}>
			<NativeTopBar
				title={tDefault(t, 'native.snapshot.create_inspection', 'Create Inspection')}
				onBack={onBack}
				backLabel=""
				action={<TopTextButton onClick={save} loading={submitLoading}>{tDefault(t, 'native.common.save', 'Save')}</TopTextButton>}
			/>
			{loading && <CenteredSpin />}
			<div style={{ paddingBottom: 24 }}>
				<FormRow label={tDefault(t, 'native.snapshot.task_name', 'Task Name')}>
					<Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={tDefault(t, 'native.snapshot.detail_information', 'Detail Information')} variant="borderless" style={formInputStyle} />
				</FormRow>
				<FormRow label={tDefault(t, 'native.snapshot.execution_date', 'Execution Date')}>
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, width: '100%' }}>
						<input type="date" value={startDate} onChange={(event) => setStartDate(event.currentTarget.value)} style={formDateStyle} />
						<input type="date" value={endDate} onChange={(event) => setEndDate(event.currentTarget.value)} style={formDateStyle} />
					</div>
				</FormRow>
				<FormRow label={tDefault(t, 'native.snapshot.execution_cycle', 'Execution Cycle')}>
					<Checkbox.Group value={week} onChange={(value) => setWeek(value.map(String))} style={checkGridStyle}>
						{ALL_WEEK.map((item) => <Checkbox key={item} value={item}>{weekdayLabel(item, t)}</Checkbox>)}
					</Checkbox.Group>
				</FormRow>
				<FormRow label={tDefault(t, 'native.snapshot.execution_month', 'Execution Month')}>
					<Checkbox.Group value={month} onChange={(value) => setMonth(value.map(String))} style={monthGridStyle}>
						{ALL_MONTHS.map((item) => <Checkbox key={item} value={item}>{item}</Checkbox>)}
					</Checkbox.Group>
				</FormRow>
				<FormRow label={tDefault(t, 'native.snapshot.assessment_template', 'Assessment Template')}>
					<Select
						value={templateKey || undefined}
						onChange={(value) => setTemplateKey(String(value))}
						placeholder={tDefault(t, 'native.snapshot.select_template', 'Select template')}
						style={{ width: '100%' }}
						options={templates.map((item) => ({ value: templateItemKey(item), label: item.item_name }))}
					/>
				</FormRow>
				<FormRow label={tDefault(t, 'native.snapshot.store', 'Store')}>
					<Select
						mode="multiple"
						value={storeIds}
						onChange={(value) => setStoreIds(value.map(String))}
						placeholder="--"
						style={{ width: '100%' }}
						options={stores.map((store) => ({ value: String(store.store_id), label: getTimedSnapshotStoreName(store) }))}
					/>
				</FormRow>
				<FormRow label={tDefault(t, 'native.snapshot.executor', 'Executor')}>
					<Select
						mode="multiple"
						value={userIds}
						onChange={(value) => setUserIds(value.map(String))}
						placeholder={tDefault(t, 'native.snapshot.select_executor', 'Select executor')}
						style={{ width: '100%' }}
						options={users.map((user) => ({ value: String(user.user_id), label: getTimedSnapshotUserName(user) }))}
					/>
				</FormRow>
				<FormRow label={tDefault(t, 'native.snapshot.device_point', 'Device')}>
					<Select
						mode="multiple"
						value={deviceKeys}
						onChange={(value) => setDeviceKeys(value.map(String))}
						placeholder={tDefault(t, 'native.snapshot.select_device', 'Select device')}
						style={{ width: '100%' }}
						options={devices.map((device) => ({ value: deviceKey(device), label: `${getStoreNameForDevice(device, stores)} / ${getTimedSnapshotDeviceName(device)}` }))}
					/>
				</FormRow>
				<FormRow label={tDefault(t, 'native.snapshot.execution_time', 'Execution Time')}>
					<div style={{ display: 'grid', gap: 8, width: '100%' }}>
						{times.map((time, index) => (
							<div key={`${time}-${index}`} style={{ display: 'flex', gap: 8 }}>
								<input
									type="time"
									step={300}
									value={time}
									onChange={(event) => {
										const next = times.slice();
										next[index] = event.currentTarget.value;
										setTimes(normalizeLocalTimes(next));
									}}
									style={formDateStyle}
								/>
								<Button danger onClick={() => setTimes((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}>
									{tDefault(t, 'native.common.delete', 'Delete')}
								</Button>
							</div>
						))}
						<Button disabled={times.length >= 10} onClick={() => setTimes((previous) => normalizeLocalTimes(previous.concat(nextTime())))}>
							{tDefault(t, 'native.snapshot.add_time', 'Add time')}
						</Button>
					</div>
				</FormRow>
				<div style={{ padding: '18px 18px 0' }}>
					<Button block type="primary" loading={submitLoading} onClick={save} style={{ background: TEAL, borderColor: TEAL, minHeight: 48 }}>
						{tDefault(t, 'native.common.save', 'Save')}
					</Button>
				</div>
			</div>
		</NativeMirrorPage>
	);
}

function PicturePreview({
	t,
	picture,
	stores,
	onBack,
}: {
	t: (key: string, options?: Record<string, unknown>) => string;
	picture: TimedSnapshotPicture;
	stores: TimedSnapshotStore[];
	onBack: () => void;
}) {
	const image = resolveExternalImageUrl(getTimedSnapshotPictureUrl(picture));
	const title = `${getPictureStoreName(picture, stores)}/${picture.alias || picture.title || picture.device_auto_id || '-'}`;
	return (
		<NativeMirrorPage style={{ background: '#f4f4f4', padding: 0 }}>
			<NativeTopBar title={title} onBack={onBack} backLabel="" />
			<div style={previewSubtitleStyle}>{formatNativeDateTime(picture.screenshot_time || picture.create_time)}</div>
			<div style={previewStageStyle}>
				{image.primary ? (
					<img
						src={image.primary}
						onError={(event) => {
							if (image.fallback && event.currentTarget.src !== image.fallback) event.currentTarget.src = image.fallback;
						}}
						alt={title}
						style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
					/>
				) : (
					<Empty description={tDefault(t, 'native.common.no_data', 'No Data')} />
				)}
			</div>
		</NativeMirrorPage>
	);
}

function SubTab({ active, children, onClick }: { active: boolean; children: ReactNode; onClick: () => void }) {
	return (
		<button type="button" onClick={onClick} style={{ ...subTabButtonStyle, color: active ? PRIMARY : MUTED }}>
			{children}
			{active && <span style={subTabLineStyle} />}
		</button>
	);
}

function BlueMetric({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div style={blueMetricStyle}>
			<div style={{ fontSize: 18 }}>{label}</div>
			<div style={{ fontSize: 30, lineHeight: 1, fontWeight: 600 }}>{value}</div>
		</div>
	);
}

function QuadMetrics({ items }: { items: Array<[string, ReactNode]> }) {
	return (
		<div style={quadMetricsStyle}>
			{items.map(([label, value], index) => (
				<div key={`${label}-${index}`} style={metricCellStyle}>
					<div style={{ fontSize: 24, color: TEXT }}>{value}</div>
					<div style={{ color: MUTED, fontSize: 14, marginTop: 10 }}>{label}</div>
				</div>
			))}
		</div>
	);
}

function TripleMetrics({ items }: { items: Array<[string, ReactNode]> }) {
	return (
		<div style={tripleMetricsStyle}>
			{items.map(([label, value]) => (
				<div key={label} style={metricCellStyle}>
					<div style={{ fontSize: 24, color: TEXT }}>{value}</div>
					<div style={{ color: TEXT, fontSize: 14, lineHeight: 1.25, marginTop: 8 }}>{label}</div>
				</div>
			))}
		</div>
	);
}

function SectionTitle({ title }: { title: string }) {
	return (
		<div style={sectionTitleStyle}>
			<span>{title}</span>
			<span style={{ color: MUTED, fontWeight: 400 }}>View All</span>
		</div>
	);
}

function RankTable({ t, rows }: { t: (key: string, options?: Record<string, unknown>) => string; rows: TimedSnapshotViolationRankRow[] }) {
	return (
		<SimpleTable
			headers={[
				tDefault(t, 'native.snapshot.items_name', 'Items Name'),
				tDefault(t, 'native.snapshot.violation_numbers', 'Violation Numbers'),
				tDefault(t, 'native.snapshot.percentage', 'Percentage'),
				tDefault(t, 'native.common.details', 'Details'),
			]}
			rows={rows.map((row) => [row.name, row.violationCount, row.percentage || '-', tDefault(t, 'native.common.details', 'Details')])}
			emptyText={tDefault(t, 'native.common.no_data', 'No Data')}
		/>
	);
}

function OperatorTable({ t, rows }: { t: (key: string, options?: Record<string, unknown>) => string; rows: TimedSnapshotOperatorRankRow[] }) {
	return (
		<SimpleTable
			headers={[
				tDefault(t, 'native.snapshot.account', 'Account'),
				tDefault(t, 'native.snapshot.full_name_role', 'Full Name (Role)'),
				tDefault(t, 'native.snapshot.to_be_processed', 'To be Processed'),
				tDefault(t, 'native.snapshot.processed', 'Processed'),
				tDefault(t, 'native.snapshot.rank', 'Rank'),
			]}
			rows={rows.map((row) => [row.account, row.fullName, row.shouldHandle, row.handled, row.rank])}
			emptyText={tDefault(t, 'native.common.no_data', 'No Data')}
		/>
	);
}

function SimpleTable({ headers, rows, emptyText }: { headers: string[]; rows: ReactNode[][]; emptyText: string }) {
	return (
		<div style={tableWrapStyle}>
			<div style={{ minWidth: headers.length * 94 }}>
				<div style={{ display: 'grid', gridTemplateColumns: `repeat(${headers.length}, minmax(88px, 1fr))`, background: '#f5f5f5' }}>
					{headers.map((header) => <div key={header} style={tableHeaderCellStyle}>{header}</div>)}
				</div>
				{rows.length === 0 ? (
					<div style={{ minHeight: 62, display: 'grid', placeItems: 'center', color: MUTED }}>{emptyText}</div>
				) : rows.map((row, index) => (
					<div key={index} style={{ display: 'grid', gridTemplateColumns: `repeat(${headers.length}, minmax(88px, 1fr))`, borderTop: '1px solid #eeeeee' }}>
						{row.map((cell, cellIndex) => <div key={cellIndex} style={tableCellStyle}>{cell}</div>)}
					</div>
				))}
			</div>
		</div>
	);
}

function TrendBlock({ title, rows, t }: { title: string; rows: Array<{ date: string; value: number }>; t: (key: string, options?: Record<string, unknown>) => string }) {
	const max = rows.reduce((acc, row) => Math.max(acc, row.value), 0);
	return (
		<div style={{ background: '#fff', marginTop: 12, padding: '16px 18px' }}>
			<div style={{ color: TEXT, fontSize: 18, marginBottom: 16 }}>{title}</div>
			{rows.length === 0 ? (
				<div style={{ minHeight: 110, display: 'grid', placeItems: 'center', color: MUTED }}>{tDefault(t, 'native.common.no_data', 'No Data')}</div>
			) : (
				<div style={{ display: 'grid', gap: 8 }}>
					{rows.map((row) => (
						<div key={row.date} style={{ display: 'grid', gridTemplateColumns: '88px 1fr 36px', gap: 8, alignItems: 'center' }}>
							<span style={{ color: MUTED }}>{row.date}</span>
							<span style={{ height: 8, background: '#eeeeee', borderRadius: 8, overflow: 'hidden' }}>
								<span style={{ display: 'block', height: 8, width: `${max > 0 ? (row.value / max) * 100 : 0}%`, background: '#6fc7ed' }} />
							</span>
							<span style={{ color: TEXT, textAlign: 'right' }}>{row.value}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function NativeEmpty({ t, onRefresh, children }: { t: (key: string, options?: Record<string, unknown>) => string; onRefresh: () => void; children?: ReactNode }) {
	return (
		<div style={emptyStyle}>
			<Empty description={tDefault(t, 'native.common.no_data', 'No Data')} />
			<Button type="text" onClick={onRefresh} style={{ color: MUTED }}>
				{tDefault(t, 'native.common.refresh', 'Refresh')}
			</Button>
			{children}
		</div>
	);
}

function CenteredSpin() {
	return (
		<div style={{ minHeight: 260, display: 'grid', placeItems: 'center' }}>
			<Spin />
		</div>
	);
}

function PictureImage({ picture }: { picture: TimedSnapshotPicture }) {
	const image = resolveExternalImageUrl(getTimedSnapshotPictureUrl(picture));
	if (!image.primary) return <div style={pictureImageStyle}><CheckCircleOutlined style={{ color: '#d7d7d7', fontSize: 26 }} /></div>;
	return (
		<div style={pictureImageStyle}>
			<img
				src={image.primary}
				onError={(event) => {
					if (image.fallback && event.currentTarget.src !== image.fallback) event.currentTarget.src = image.fallback;
				}}
				alt=""
				style={{ width: '100%', height: '100%', objectFit: 'cover' }}
			/>
		</div>
	);
}

function IconButton({ label, children, onClick }: { label: string; children: ReactNode; onClick: () => void }) {
	return (
		<button type="button" aria-label={label} onClick={onClick} style={iconButtonStyle}>
			{children}
		</button>
	);
}

function TopTextButton({ children, loading, onClick }: { children: ReactNode; loading?: boolean; onClick: () => void }) {
	return (
		<Button type="text" loading={loading} onClick={onClick} style={{ color: PRIMARY, height: 48, padding: 0, width: 58, fontSize: 16 }}>
			{children}
		</Button>
	);
}

function FormRow({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div style={formRowStyle}>
			<div style={formLabelStyle}>{label}</div>
			<div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>{children}</div>
		</div>
	);
}

function TaskCounter({ value, label }: { value: ReactNode; label: string }) {
	return (
		<div style={{ textAlign: 'center', minWidth: 0 }}>
			<div style={{ color: TEXT, fontSize: 28, lineHeight: 1.1 }}>{value}</div>
			<div style={{ color: TEXT, fontSize: 15, lineHeight: 1.15, marginTop: 8 }}>{label}</div>
		</div>
	);
}

function numberFromRecord(record: Record<string, unknown> | undefined, keys: string[]): number {
	if (!record) return 0;
	for (const key of keys) {
		const value = record[key];
		if (typeof value === 'number' && Number.isFinite(value)) return value;
		if (typeof value === 'string' && value.trim()) {
			const parsed = Number(value);
			if (Number.isFinite(parsed)) return parsed;
		}
	}
	return 0;
}

function groupPicturesByDate(pictures: TimedSnapshotPicture[]): Array<{ label: string; items: TimedSnapshotPicture[] }> {
	const sorted = pictures.slice().sort((a, b) => String(b.screenshot_time || b.create_time || '').localeCompare(String(a.screenshot_time || a.create_time || '')));
	const groups: Array<{ label: string; items: TimedSnapshotPicture[] }> = [];
	const indexByLabel = new Map<string, number>();
	for (const picture of sorted) {
		const time = String(picture.screenshot_time || picture.create_time || '');
		const label = time.length >= 10 ? time.slice(0, 10) : '-';
		const existing = indexByLabel.get(label);
		if (existing == null) {
			indexByLabel.set(label, groups.length);
			groups.push({ label, items: [picture] });
		} else {
			groups[existing]?.items.push(picture);
		}
	}
	return groups;
}

function getPictureStoreName(picture: TimedSnapshotPicture, stores: TimedSnapshotStore[]): string {
	if (picture.store_name) return picture.store_name;
	const match = stores.find((store) => String(store.store_id) === String(picture.store_id));
	return match ? getTimedSnapshotStoreName(match) : String(picture.store_id || '-');
}

function extractPictureTemplateItem(picture: TimedSnapshotPicture): TimedSnapshotTemplateItem | undefined {
	const candidates = Array.isArray(picture.items) ? picture.items : [];
	for (const candidate of candidates) {
		if (!candidate || typeof candidate !== 'object') continue;
		const record = candidate as Record<string, unknown>;
		const itemId = record.item_id ?? record.items_id ?? record.id;
		const itemName = record.item_name ?? record.title ?? record.name;
		const categoryId = record.category_id;
		const templateId = record.template_id;
		if (itemId != null && itemName != null && categoryId != null && templateId != null) {
			return {
				item_id: String(itemId),
				item_name: String(itemName),
				category_id: String(categoryId),
				template_id: String(templateId),
				raw: record,
			};
		}
	}
	return undefined;
}

function getPlanStoreCount(plan: TimedSnapshotPlan): number {
	if (Array.isArray(plan.store_list)) return plan.store_list.length;
	const count = Number(plan.store_count);
	return Number.isFinite(count) ? count : 0;
}

function getPlanDateRange(plan: TimedSnapshotPlan): string {
	const date = plan.execute_date;
	const start = date && typeof date === 'object' && 'start_time' in date ? String(date.start_time || '') : '';
	const end = date && typeof date === 'object' && 'end_time' in date ? String(date.end_time || '') : '';
	if (start || end) return `${start || '-'}~${end || '-'}`;
	return '-';
}

function getPlanCycle(plan: TimedSnapshotPlan, t: (key: string, options?: Record<string, unknown>) => string): string {
	const month = Array.isArray(plan.month) ? plan.month.join(' ') : '';
	const week = Array.isArray(plan.week) ? plan.week.map((item) => weekdayLabel(String(item), t)).join(', ') : '';
	return [month, week].filter(Boolean).join(' / ') || '-';
}

function weekdayLabel(value: string, t: (key: string, options?: Record<string, unknown>) => string): string {
	const labels: Record<string, string> = {
		'1': tDefault(t, 'native.snapshot.mon', 'Mon'),
		'2': tDefault(t, 'native.snapshot.tues', 'Tues'),
		'3': tDefault(t, 'native.snapshot.wed', 'Wed'),
		'4': tDefault(t, 'native.snapshot.thur', 'Thur'),
		'5': tDefault(t, 'native.snapshot.fri', 'Fri'),
		'6': tDefault(t, 'native.snapshot.sat', 'Sat'),
		'7': tDefault(t, 'native.snapshot.sun', 'Sun'),
	};
	return labels[value] || value;
}

function deviceKey(device: TimedSnapshotDevice): string {
	return `${String(device.device_auto_id)}:${String(device.channel_id ?? '1')}`;
}

function templateItemKey(item: TimedSnapshotTemplateItem): string {
	return `${String(item.template_id)}:${String(item.category_id)}:${String(item.item_id)}`;
}

function getStoreNameForDevice(device: TimedSnapshotDevice, stores: TimedSnapshotStore[]): string {
	const match = stores.find((store) => String(store.store_id) === String(device.store_id));
	return match ? getTimedSnapshotStoreName(match) : String(device.store_id || '-');
}

function normalizeLocalTimes(times: string[]): string[] {
	const seen = new Set<string>();
	for (const time of times) {
		if (/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) seen.add(time);
	}
	return Array.from(seen).sort().slice(0, 10);
}

function nextTime(): string {
	const date = new Date();
	date.setMinutes(date.getMinutes() + 5);
	const roundedMinutes = Math.ceil(date.getMinutes() / 5) * 5;
	if (roundedMinutes >= 60) {
		date.setHours(date.getHours() + 1);
		date.setMinutes(0);
	} else {
		date.setMinutes(roundedMinutes);
	}
	date.setSeconds(0, 0);
	return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const topBarStyle: CSSProperties = {
	height: 56,
	background: '#fff',
	borderBottom: '1px solid #eeeeee',
	display: 'grid',
	gridTemplateColumns: '48px minmax(0, 1fr) 48px',
	alignItems: 'center',
	position: 'sticky',
	top: 0,
	zIndex: 10,
};

const backButtonStyle: CSSProperties = {
	border: 0,
	background: 'transparent',
	height: 56,
	color: PRIMARY,
	fontSize: 18,
	cursor: 'pointer',
};

const backChevronStyle: CSSProperties = {
	display: 'inline-block',
	width: 12,
	height: 12,
	borderLeft: '2px solid currentColor',
	borderBottom: '2px solid currentColor',
	transform: 'rotate(45deg)',
};

const topTabsStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	alignItems: 'center',
	height: 56,
	textAlign: 'center',
};

const topTabButtonStyle: CSSProperties = {
	position: 'relative',
	height: 56,
	border: 0,
	background: '#fff',
	fontSize: 17,
	cursor: 'pointer',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	padding: '0 3px',
};

const topTabLineStyle: CSSProperties = {
	position: 'absolute',
	left: '35%',
	right: '35%',
	bottom: 0,
	height: 2,
	background: PRIMARY,
	borderRadius: 2,
};

const iconButtonStyle: CSSProperties = {
	width: 48,
	height: 56,
	border: 0,
	background: 'transparent',
	color: '#111',
	fontSize: 24,
	cursor: 'pointer',
};

const filterPanelStyle: CSSProperties = {
	background: '#fff',
	borderBottom: '1px solid #eeeeee',
	padding: '10px 12px',
	display: 'flex',
	gap: 8,
	flexWrap: 'wrap',
	alignItems: 'end',
};

const dateInputStyle: CSSProperties = {
	width: '100%',
	height: 32,
	border: '1px solid #d9dce3',
	borderRadius: 6,
	padding: '0 7px',
	font: 'inherit',
	background: '#fff',
	boxSizing: 'border-box',
};

const subTabsStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
	background: '#fff',
	borderBottom: '1px solid #eeeeee',
};

const dataTabsStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	background: '#fff',
	borderBottom: '1px solid #eeeeee',
};

const subTabButtonStyle: CSSProperties = {
	position: 'relative',
	minHeight: 52,
	border: 0,
	background: '#fff',
	fontSize: 16,
	lineHeight: 1.15,
	cursor: 'pointer',
	padding: '0 5px',
};

const subTabLineStyle: CSSProperties = {
	position: 'absolute',
	left: '36%',
	right: '36%',
	bottom: 0,
	height: 2,
	background: PRIMARY,
	borderRadius: 2,
};

const nativeFilterLineStyle: CSSProperties = {
	minHeight: 48,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-around',
	gap: 10,
	background: '#fff',
	color: MUTED,
	fontSize: 16,
	borderBottom: '1px solid #eeeeee',
};

const blueMetricStyle: CSSProperties = {
	margin: '28px 18px 18px',
	minHeight: 84,
	borderRadius: 2,
	background: 'linear-gradient(90deg, #61b7ee, #9adfed)',
	color: '#fff',
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr) auto',
	alignItems: 'center',
	padding: '0 28px',
};

const quadMetricsStyle: CSSProperties = {
	background: '#fff',
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
	rowGap: 30,
	padding: '4px 0 28px',
};

const tripleMetricsStyle: CSSProperties = {
	background: '#fff',
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	padding: '28px 0',
};

const metricCellStyle: CSSProperties = {
	minHeight: 78,
	display: 'grid',
	placeItems: 'center',
	textAlign: 'center',
	padding: '0 6px',
};

const sectionTitleStyle: CSSProperties = {
	background: '#fff',
	minHeight: 54,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '0 18px',
	color: TEXT,
	fontSize: 20,
};

const tableWrapStyle: CSSProperties = {
	background: '#fff',
	overflowX: 'auto',
	padding: '0 12px 14px',
};

const tableHeaderCellStyle: CSSProperties = {
	minHeight: 42,
	display: 'grid',
	placeItems: 'center',
	textAlign: 'center',
	color: TEXT,
	fontSize: 13,
	padding: '4px 6px',
};

const tableCellStyle: CSSProperties = {
	minHeight: 42,
	display: 'grid',
	placeItems: 'center',
	textAlign: 'center',
	color: TEXT,
	fontSize: 13,
	padding: '4px 6px',
};

const emptyStyle: CSSProperties = {
	minHeight: 440,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 6,
	background: '#fff',
};

const timelineGroupStyle: CSSProperties = {
	position: 'relative',
	padding: '0 0 24px 20px',
	borderLeft: '1px solid #eeeeee',
};

const timelineMarkerStyle: CSSProperties = {
	position: 'absolute',
	left: -5,
	top: 3,
	width: 9,
	height: 9,
	borderRadius: '50%',
	border: '1px solid #eeeeee',
	background: '#fff',
};

const timelineLabelStyle: CSSProperties = {
	color: MUTED,
	fontSize: 17,
	lineHeight: 1.2,
	marginBottom: 12,
};

const pictureGridStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	gap: 12,
};

const pictureCardStyle: CSSProperties = {
	minWidth: 0,
};

const pictureButtonStyle: CSSProperties = {
	border: 0,
	background: '#fff',
	padding: 0,
	width: '100%',
	textAlign: 'center',
	cursor: 'pointer',
};

const pictureImageStyle: CSSProperties = {
	width: '100%',
	aspectRatio: '4 / 3',
	background: 'linear-gradient(135deg, #f2f2f2, #fbfbfb 42%, #eeeeee)',
	display: 'grid',
	placeItems: 'center',
	overflow: 'hidden',
};

const pictureCaptionStyle: CSSProperties = {
	display: 'block',
	color: TEXT,
	fontSize: 15,
	lineHeight: 1.35,
	marginTop: 6,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const pictureTimeStyle: CSSProperties = {
	display: 'block',
	color: MUTED,
	fontSize: 12,
	lineHeight: 1.25,
	marginTop: 2,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const taskCardStyle: CSSProperties = {
	borderTop: '1px solid #eeeeee',
	padding: '18px 18px 22px',
	background: '#fff',
};

const taskTitleRowStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
	alignItems: 'center',
	gap: 10,
	minHeight: 34,
};

const statusDotStyle: CSSProperties = {
	width: 9,
	height: 9,
	borderRadius: '50%',
};

const taskTitleStyle: CSSProperties = {
	color: TEXT,
	fontSize: 21,
	lineHeight: 1.25,
};

const taskCountersStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
	gap: 8,
	marginTop: 26,
};

const taskInfoBoxStyle: CSSProperties = {
	background: '#f6f6f6',
	color: MUTED,
	fontSize: 17,
	lineHeight: 1.9,
	marginTop: 22,
	padding: '14px 18px',
};

const taskActionsStyle: CSSProperties = {
	display: 'flex',
	justifyContent: 'flex-end',
	gap: 10,
	marginTop: 12,
};

const formRowStyle: CSSProperties = {
	minHeight: 68,
	borderBottom: '1px solid #eeeeee',
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	padding: '10px 18px',
	background: '#fff',
};

const formLabelStyle: CSSProperties = {
	flex: '0 0 138px',
	color: TEXT,
	fontSize: 20,
	lineHeight: 1.2,
};

const formInputStyle: CSSProperties = {
	textAlign: 'right',
	color: MUTED,
	fontSize: 18,
};

const formDateStyle: CSSProperties = {
	width: '100%',
	height: 34,
	border: '1px solid #d9dce3',
	borderRadius: 4,
	padding: '0 8px',
	font: 'inherit',
	boxSizing: 'border-box',
};

const checkGridStyle: CSSProperties = {
	width: '100%',
	display: 'grid',
	gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	gap: 8,
};

const monthGridStyle: CSSProperties = {
	width: '100%',
	display: 'grid',
	gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
	gap: 8,
};

const previewSubtitleStyle: CSSProperties = {
	minHeight: 40,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: '#fff',
	color: '#666',
	fontSize: 15,
	borderBottom: '1px solid #eeeeee',
};

const previewStageStyle: CSSProperties = {
	minHeight: 'calc(100vh - 88px)',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
	background: '#f4f4f4',
	paddingTop: 88,
};
